import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { IonicModule, ToastController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';
import { modeFavicon, modeNev, NEM_ONALLO_MODOK } from '../../mode-nevek';
import { FOKUSZ_KEPES, MODUL_TEMAK, ModulTema, kijeloltTema, temaBeallitas, temaNev } from '../../modul-tema';

interface ModulSor {
  kod: string;
  nev: string;
  ikon: string;
  fokuszKepes: boolean;
  /** Az érvényes téma (az admin döntése, vagy ha nem döntött, az alapérték). */
  kijelolt: ModulTema;
  /** Az admin kifejezetten beállította-e (különben az alapérték él). */
  beallitott: boolean;
  /** Módosíthatja-e a belépő (a modul adminja vagy az általános admin – a kiszolgáló dönti). */
  admin: boolean;
  fut: boolean;
}

/**
 *  ADMIN → RENDSZER → MODULOK TÉMÁJA.
 *
 *  Egy modul Klasszikus (bal menüsáv) vagy Fókusz (felső sáv, profil-lenyíló, bal sáv nélkül)
 *  keretben jelenik meg – ezt az intézmény adminja dönti el, feladatellátásonként
 *  (`feladatellatas.mode_tema`, backend/modul-tema.js). Itt az intézmény összes modulja egy
 *  helyen: mi az érvényes kialakítás, és – akinek joga van – át is állítható.
 *
 *  Fókuszban csak az a modul jelenhet meg, amelyiknek elkészült a Fókusz-kerete (FOKUSZ_KEPES);
 *  a többinél a bal sáv nélkül nem lenne mivel navigálni, ezért azok Klasszikusak maradnak.
 */
@Component({
  selector: 'app-admin-modul-tema',
  standalone: true,
  imports: [CommonModule, IonicModule],
  templateUrl: './modul-tema.component.html',
  styleUrls: ['./modul-tema.component.scss'],
})
export class AdminModulTemaComponent implements OnInit {
  readonly temak = MODUL_TEMAK;
  temaNev = temaNev;

  feladatellatas = '';
  intezmenyNev = '';
  sorok: ModulSor[] = [];
  betolt = false;
  hiba = '';

  constructor(private http: HttpClient, private data: DataService, private toast: ToastController) {}

  get vanAdmin(): boolean { return this.sorok.some((s) => s.admin); }

  async ngOnInit() {
    const intezmeny = await this.data.getFromStorage('sajatIntezmeny');
    this.feladatellatas = String(intezmeny?.intezmenyId || '').trim();
    this.intezmenyNev = intezmeny?.intezmenyName || this.feladatellatas;
    const modok = this.modulok(intezmeny?.mode);
    if (!modok.length) {
      const aktualis = String((await this.data.getFromStorage('currentMode')) || '');
      if (aktualis) modok.push(aktualis);
    }
    await this.betoltMind(modok);
  }

  /** A feladatellátás moduljai: tömb vagy JSON-szöveg, elemei kódok vagy {name} objektumok. */
  private modulok(nyers: any): string[] {
    let lista: any = nyers;
    if (typeof lista === 'string') {
      try { lista = JSON.parse(lista); } catch { lista = lista.split(','); }
    }
    if (!Array.isArray(lista)) return [];
    const kodok = lista
      .map((m: any) => String(typeof m === 'object' ? m?.name ?? '' : m ?? '').trim().toUpperCase())
      .filter((m: string) => m && !NEM_ONALLO_MODOK.has(m));
    return [...new Set<string>(kodok)];
  }

  private async betoltMind(modok: string[]) {
    if (!this.feladatellatas) { this.hiba = 'Nincs kiválasztott intézmény.'; return; }
    this.betolt = true;
    this.hiba = '';
    try {
      // Az admin-jog modulonként más lehet (a modul adminja csak a saját modulját állíthatja),
      // ezért modulonként kérdezünk; a beállítás maga mindegyik válaszban ugyanaz.
      const valaszok = await Promise.all(modok.map((m) =>
        firstValueFrom(this.http.get<any>(`/api/modul-tema/${this.feladatellatas}?mode=${encodeURIComponent(m)}`))
          .catch(() => null)));
      const elso = valaszok.find((v) => v && v.tema !== undefined);
      if (!elso) { this.hiba = 'A modulok témája nem tölthető be.'; return; }
      const tema = temaBeallitas(elso.tema);
      this.sorok = modok.map((kod, i) => this.sor(kod, tema, !!valaszok[i]?.admin));
    } finally {
      this.betolt = false;
    }
  }

  private sor(kod: string, tema: Record<string, string>, admin: boolean): ModulSor {
    const fokuszKepes = FOKUSZ_KEPES.has(kod);
    return {
      kod, nev: modeNev(kod), ikon: modeFavicon(kod), fokuszKepes,
      kijelolt: kijeloltTema(kod, tema),
      beallitott: fokuszKepes && (tema[kod] === 'klasszikus' || tema[kod] === 'fokusz'),
      admin, fut: false,
    };
  }

  /** Téma választása ('' = vissza az alapértékre). */
  async beallit(s: ModulSor, uj: string) {
    if (!s.admin || s.fut) return;
    if (uj && uj === s.kijelolt && s.beallitott) return;
    const regi = { kijelolt: s.kijelolt, beallitott: s.beallitott };
    s.fut = true;
    if (uj === 'klasszikus' || uj === 'fokusz') s.kijelolt = uj;
    try {
      const r: any = await firstValueFrom(this.http.put(`/api/modul-tema/${this.feladatellatas}`, { mode: s.kod, tema: uj }));
      const tema = temaBeallitas(r?.tema);
      Object.assign(s, this.sor(s.kod, tema, s.admin));
      await this.uzen(uj
        ? `${s.nev}: ${temaNev(s.kijelolt)} kialakítás beállítva. Mindenkinél a modul következő megnyitásakor lép életbe.`
        : `${s.nev}: visszaállítva az alapértékre (${temaNev(s.kijelolt)}).`, 'success');
    } catch (e: any) {
      Object.assign(s, regi);
      await this.uzen(e?.error?.error || 'A téma mentése nem sikerült.', 'danger');
    } finally {
      s.fut = false;
    }
  }

  private async uzen(message: string, color: string) {
    try { (await this.toast.create({ message, color, duration: 3200 })).present(); } catch { /* nem kritikus */ }
  }
}
